// Rail shape resolution (RED-5, M16.2). PURE (ENG-1). A rail's `shape` state is
// derived from which of its 4 horizontal neighbours also hold a rail — straight,
// curved, or (deferred) ascending. Powered/detector rails never curve. The same
// connection table drives the minecart physics (entities/minecart.ts).
import { DIR_VEC, type Facing } from './redstone/types';

/** Horizontal-only opposite (the redstone `OPPOSITE` widens to include up/down). */
export const OPP_FACING: Record<Facing, Facing> = { north: 'south', south: 'north', east: 'west', west: 'east' };

/** The ten Java rail shapes. Ascending_* are resolved but ridden as flat in this
 *  slice (slope physics is M16.2-remainder). */
export type RailShape =
  | 'north_south' | 'east_west'
  | 'ascending_east' | 'ascending_west' | 'ascending_north' | 'ascending_south'
  | 'south_east' | 'south_west' | 'north_west' | 'north_east';

/** The two edge directions a rail shape connects (where the track leaves the cell).
 *  A cart entering from one connection leaves by the other. */
export const RAIL_CONNECTIONS: Record<RailShape, readonly [Facing, Facing]> = {
  north_south: ['north', 'south'],
  east_west: ['east', 'west'],
  // Ascending: the horizontal exits are the same as the straight rail; the raised
  // end is named first (the cart climbs one block leaving that way, RED-5 slopes).
  ascending_north: ['north', 'south'],
  ascending_south: ['south', 'north'],
  ascending_east: ['east', 'west'],
  ascending_west: ['west', 'east'],
  south_east: ['south', 'east'],
  south_west: ['south', 'west'],
  north_west: ['north', 'west'],
  north_east: ['north', 'east'],
};

/** The raised end of an ascending rail (where the track climbs one block), or null
 *  for a flat rail. A cart leaving by this end rises +1; entering from it descends. */
export function raisedEnd(shape: RailShape): Facing | null {
  switch (shape) {
    case 'ascending_north': return 'north';
    case 'ascending_south': return 'south';
    case 'ascending_east': return 'east';
    case 'ascending_west': return 'west';
    default: return null;
  }
}

/** What the resolver reads: is there a rail at a cell? */
export interface RailView {
  isRail(x: number, y: number, z: number): boolean;
}

/**
 * The shape a rail at (x,y,z) should take from its neighbours. `straightOnly`
 * (powered/detector rails) never curve. A rail connects to a neighbour at the same
 * level, one block up (it ascends toward it), or one block down (the neighbour
 * ascends toward this rail). Curves take priority over straights when two
 * perpendicular same-level neighbours are present. Finally, a straight rail with a
 * rail one block *up* on one end becomes the matching `ascending_*` slope (RED-5).
 */
export function resolveRailShape(view: RailView, x: number, y: number, z: number, straightOnly = false): RailShape {
  const conn = (d: Facing): boolean => {
    const [dx, , dz] = DIR_VEC[d];
    return view.isRail(x + dx, y, z + dz) || view.isRail(x + dx, y + 1, z + dz) || view.isRail(x + dx, y - 1, z + dz);
  };
  const up = (d: Facing): boolean => {
    const [dx, , dz] = DIR_VEC[d];
    return view.isRail(x + dx, y + 1, z + dz);
  };
  const n = conn('north'), s = conn('south'), e = conn('east'), w = conn('west');
  let shape: RailShape;
  if (straightOnly) {
    shape = e || w ? 'east_west' : 'north_south';
  } else if (s && e && !w && !n) shape = 'south_east';
  else if (s && w && !e && !n) shape = 'south_west';
  else if (n && e && !w && !s) shape = 'north_east';
  else if (n && w && !e && !s) shape = 'north_west';
  else if (e || w) shape = 'east_west';
  else if (n || s) shape = 'north_south';
  else shape = 'north_south'; // isolated rail
  // A straight rail with a raised neighbour on one end tilts into that slope.
  if (shape === 'east_west') {
    if (up('east')) return 'ascending_east';
    if (up('west')) return 'ascending_west';
  } else if (shape === 'north_south') {
    if (up('north')) return 'ascending_north';
    if (up('south')) return 'ascending_south';
  }
  return shape;
}

/** The connection a cart leaves by, given the one it entered through. Returns null
 *  if the entry side isn't one of the shape's connections (the rails don't meet). */
export function exitConnection(shape: RailShape, entry: Facing): Facing | null {
  const [a, b] = RAIL_CONNECTIONS[shape];
  if (entry === a) return b;
  if (entry === b) return a;
  return null;
}

/** True if `dir` is one of a shape's two connections. */
export function connectsToward(shape: RailShape, dir: Facing): boolean {
  const [a, b] = RAIL_CONNECTIONS[shape];
  return dir === a || dir === b;
}

/** The side opposite a heading — used to derive the entry side from a heading. */
export const railEntryFrom = (dir: Facing): Facing => OPP_FACING[dir];
